import { smoothTo } from './params';

/**
 * A 2×2 gain matrix on a stereo signal:
 *   outL = leftFromLeft·L + leftFromRight·R,  outR = rightFromLeft·L + rightFromRight·R.
 * Whatever is connected to `input` is first up- or down-mixed to stereo.
 */
export interface StereoMatrix {
  readonly input: AudioNode;
  readonly output: AudioNode;
  set(leftFromLeft: number, leftFromRight: number, rightFromLeft: number, rightFromRight: number, immediate?: boolean): void;
  destroy(): void;
}

export function createStereoMatrix(context: BaseAudioContext): StereoMatrix {
  const input = new GainNode(context, { channelCount: 2, channelCountMode: 'explicit', channelInterpretation: 'speakers' });
  const splitter = new ChannelSplitterNode(context, { numberOfOutputs: 2 });
  const merger = new ChannelMergerNode(context, { numberOfInputs: 2 });
  // Order: LL, LR, RL, RR (destination channel first).
  const gains = [1, 0, 0, 1].map((gain) => new GainNode(context, { gain }));
  input.connect(splitter);
  gains.forEach((gain, i) => {
    splitter.connect(gain, i % 2);
    gain.connect(merger, 0, i >> 1);
  });

  return {
    input,
    output: merger,
    set(...args) {
      const immediate = args[4] === true;
      gains.forEach((gain, i) => {
        const value = args[i] as number;
        if (immediate) gain.gain.value = value;
        else smoothTo(context, gain.gain, value);
      });
    },
    destroy() {
      input.disconnect();
      splitter.disconnect();
      for (const gain of gains) gain.disconnect();
      merger.disconnect();
    },
  };
}
